<template>
    <BaseDialog
        :open="true"
        :title="kind === 'person' ? t('chat.mentions.addPerson') : t('chat.mentions.addTeam')"
        :show-close="!submitting"
        :disable-close-on-backdrop-click="submitting"
        :return-focus-to="returnFocusTo"
        :reserve-mobile-nav-space="false"
        @update:open="
            (open: boolean) => {
                if (!open && !submitting) cancel();
            }
        ">
        <form v-if="kind === 'person'" ref="personForm" class="flex flex-col gap-4 fs-mask" @submit.prevent="createPerson">
            <FormField :label="t('chat.mentions.name')" required>
                <BaseInput v-model="name" :disabled="submitting" maxlength="255" required />
            </FormField>
            <FormField :label="t('chat.mentions.jobTitle')" required>
                <BaseInput v-model="jobTitle" :disabled="submitting" maxlength="255" required />
            </FormField>
            <div role="alert">
                <p v-if="personError" class="body-small text-ink">{{ t("chat.mentions.createError") }}</p>
            </div>
            <div class="flex justify-end gap-2">
                <BaseButton theme="secondary" :disabled="submitting" @click="cancel()">{{ t("common.cancel") }}</BaseButton>
                <BaseButton theme="primary" type="submit" :disabled="submitting || !name.trim() || !jobTitle.trim()">{{
                    submitting ? t("chat.mentions.saving") : t("chat.mentions.addPerson")
                }}</BaseButton>
            </div>
        </form>
        <template v-else>
            <p role="status" class="body-small text-ink-muted empty:sr-only">
                <template v-if="loading">{{ t("chat.mentions.loadingForm") }}</template>
            </p>
            <div role="alert">
                <template v-if="loadError">
                    <p class="body-small text-ink">{{ t("chat.mentions.formError") }}</p>
                    <BaseButton theme="secondary" size="sm" @click="loadTeamForm">{{ t("chat.mentions.retry") }}</BaseButton>
                </template>
            </div>
            <div ref="teamFormContainer">
                <TeamCreateForm
                    v-if="teamForm"
                    :source="TEAM_INTERACTION_SOURCE.CHAT_COMPOSER"
                    embedded
                    title-shown-above
                    class="fs-mask"
                    :initial-name="initialName"
                    :creator="teamForm.creator"
                    :search-url="teamForm.search_url"
                    :create-url="teamForm.create_url"
                    :team-url-template="teamForm.team_url_template"
                    :recommendations-url="teamForm.recommendations_url"
                    :back-url="teamForm.back_url"
                    @submitting="submitting = $event"
                    @selected="teamSelected" />
            </div>
            <div class="flex justify-end">
                <BaseButton theme="secondary" :disabled="submitting" @click="cancel()">{{ t("common.cancel") }}</BaseButton>
            </div>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { MentionSuggestion } from "~vue/api/generated/connections";
import type { TeamCreatePage, TeamPerspectiveSummary } from "~vue/api/generated/team-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import TeamCreateForm from "~vue/Connections/TeamCreateForm.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

type CreatedConnection = { id: string; persona: { name: string }; job_title: string };
const props = defineProps<{ kind: MentionSuggestion["kind"]; initialName: string; returnFocusTo: HTMLTextAreaElement | null | undefined }>();
const emit = defineEmits<{ created: [suggestion: MentionSuggestion]; cancel: [] }>();
const { t } = useI18n();
const teamFormContainer = useTemplateRef<HTMLElement>("teamFormContainer");
const personForm = useTemplateRef<HTMLFormElement>("personForm");
onMounted(() => {
    void nextTick(() => personForm.value?.querySelector<HTMLInputElement>("input")?.focus());
});
const name = ref(props.initialName);
const jobTitle = ref("");
const submitting = ref(false);
const personError = ref(false);
const loading = ref(false);
const loadError = ref(false);
const teamForm = ref<TeamCreatePage | null>(null);
let disposed = false;
onBeforeUnmount(() => {
    disposed = true;
});
function cancel() {
    emit("cancel");
}

async function createPerson() {
    if (submitting.value || !name.value.trim() || !jobTitle.value.trim()) {
        return;
    }
    submitting.value = true;
    personError.value = false;
    try {
        const title = jobTitle.value.trim();
        const { data, error } = await useFetch("/api/connections/")
            .post({ persona: { name: name.value.trim(), relationship: title }, job_title: title, in_team: false, in_org: false, influence: 2 })
            .json<CreatedConnection>();
        if (disposed) {
            return;
        }
        if (error.value || !data.value) {
            personError.value = true;
            return;
        }
        emit("created", { kind: "person", entity_id: data.value.id, label: data.value.persona.name, description: data.value.job_title });
    } catch {
        if (!disposed) {
            personError.value = true;
        }
    } finally {
        submitting.value = false;
    }
}
async function loadTeamForm() {
    if (loading.value || disposed) {
        return;
    }
    loading.value = true;
    loadError.value = false;
    try {
        const { data, error } = await useFetch("/perspective/teams/create-form/").get().json<TeamCreatePage>();
        if (disposed) {
            return;
        }
        if (error.value || !data.value) {
            loadError.value = true;
        } else {
            teamForm.value = data.value;
            await nextTick();
            if (!disposed) {
                teamFormContainer.value?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
            }
        }
    } catch {
        if (!disposed) {
            loadError.value = true;
        }
    } finally {
        loading.value = false;
    }
}
function teamSelected(team: TeamPerspectiveSummary) {
    if (!disposed) {
        emit("created", { kind: "team", entity_id: team.id, label: team.name, description: null });
    }
}
if (props.kind === "team") {
    void loadTeamForm();
}
</script>
